// DiagramBuilder.tsx — a THIN wrapper over React Flow (Req 10, 11, 19.2).
//
// This component owns NONE of the diagram application logic. Every structural
// mutation (add node, connect nodes, relabel, set protocol, cascade-delete,
// add/move/resize/remove annotations) is delegated to the pure, library-agnostic
// `diagramState.ts` module operating on the plain `Diagram_State` JSON shape.
// React Flow is used ONLY to render that state and to capture pointer
// interaction. This keeps the canvas library swappable behind the `DiagramState`
// contract.
//
// Because this module statically imports `reactflow` (and its stylesheet), it
// MUST be reached through a dynamic import so React Flow stays out of the
// initial bundle (Req 19.3). The `LazyDiagramBuilder` wrapper does exactly that.

import React, {
       useCallback,
       useEffect,
       useMemo,
       useRef,
       useState,
} from 'react';
import ReactFlow, {
       Background,
       BackgroundVariant,
       Controls,
       Handle,
       Position,
       MarkerType,
       NodeResizer,
       useNodesState,
       useEdgesState,
       applyEdgeChanges,
       type Connection,
       type Edge as RFEdge,
       type EdgeChange,
       type Node as RFNode,
       type NodeProps,
       type OnSelectionChangeParams,
       type ReactFlowInstance,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
       Trash2,
       AlertCircle,
       X,
       Monitor,
       Server,
       Database,
       Zap,
       Layers,
       Globe,
       HardDrive,
       Box,
       Plus,
       Maximize2,
       Minimize2,
       Square,
       Minus,
       Type as TypeIcon,
       Pencil,
       type LucideIcon,
} from 'lucide-react';
import {
       DiagramState,
       DiagramNode,
       DiagramEdge,
       DiagramAnnotation,
       AnnotationKind,
       NodeType,
       EdgeProtocol,
} from '../../types/hld';
import {
       addNode,
       addEdge,
       removeNode,
       setNodeLabel,
       setEdgeLabel,
       setEdgeProtocol,
       setEdgeBidirectional,
       addAnnotation,
       updateAnnotation,
       removeAnnotation,
       makeAnnotationId,
       serialize,
       NODE_TYPES,
       EDGE_PROTOCOLS,
       DEFAULT_GROUP_SIZE,
       DEFAULT_DIVIDER_SIZE,
       type DiagramError,
} from '../../services/diagramState';

// ─── Props ─────────────────────────────────────────────────────────────────

export interface DiagramBuilderProps {
       value: DiagramState;
       onChange: (next: DiagramState) => void;
       readOnly?: boolean;
}

// ─── Node type metadata (component kinds) ──────────────────────────────────

const NODE_TYPE_META: Record<
       NodeType,
       { label: string; color: string; Icon: LucideIcon }
> = {
       // The canvas uses color to distinguish system roles. Keep the frame neutral;
       // these are semantic markers, not an application-wide theme.
       client: { label: 'Client', color: '#60a5fa', Icon: Monitor },
       service: { label: 'Service', color: '#34d399', Icon: Server },
       db: { label: 'Database', color: '#a7f3d0', Icon: Database },
       cache: { label: 'Cache', color: '#fbbf24', Icon: Zap },
       queue: { label: 'Queue', color: '#fb7185', Icon: Layers },
       cdn: { label: 'CDN', color: '#67e8f9', Icon: Globe },
       storage: { label: 'Storage', color: '#94a3b8', Icon: HardDrive },
       custom: { label: 'Custom', color: '#a1a1aa', Icon: Box },
};

// ─── Stable handler bag passed to custom nodes via data (avoids stale closures) ─

interface NodeHandlers {
       onAnnotationResizeEnd: (id: string, box: { x: number; y: number; width: number; height: number }) => void;
       onAnnotationResizeLive: (id: string, width: number, height: number) => void;
       onRequestEdit: (id: string) => void;
}

// ─── Custom node: architecture component (icon + type + label) ─────────────

interface HldNodeData {
       label: string;
       nodeType: NodeType;
       readOnly: boolean;
       handlers: React.MutableRefObject<NodeHandlers>;
}

const HldFlowNode: React.FC<NodeProps<HldNodeData>> = ({ id, data, selected }) => {
       const meta = NODE_TYPE_META[data.nodeType];
       const Icon = meta.Icon;
       return (
              <div
                     className={`group relative flex min-w-[160px] items-center gap-3 rounded-xl border bg-white px-4 py-2.5 shadow-sm dark:bg-[#202024] ${selected ? 'shadow-md ring-2 ring-offset-1 dark:ring-offset-[#09090b]' : ''
                            }`}
                     style={{ borderColor: meta.color, borderWidth: 1.5, ['--tw-ring-color' as string]: meta.color }}
              >
                     <Handle type="target" position={Position.Left} className="!h-3 !w-3 !border-2 !border-white dark:!border-[#202024]" style={{ background: meta.color }} />
                     <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: `${meta.color}1a`, color: meta.color }}>
                            <Icon className="h-4.5 w-4.5" aria-hidden="true" />
                     </span>
                     <span className="min-w-0">
                            <span className="block text-[10px] font-semibold uppercase tracking-wide" style={{ color: meta.color }}>{meta.label}</span>
                            <span className="block truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{data.label}</span>
                     </span>
                     <Handle type="source" position={Position.Right} className="!h-3 !w-3 !border-2 !border-white dark:!border-[#202024]" style={{ background: meta.color }} />
                     {!data.readOnly && (
                            <button
                                   type="button"
                                   title="Edit"
                                   onPointerDown={(e) => e.stopPropagation()}
                                   onClick={(e) => { e.stopPropagation(); data.handlers.current.onRequestEdit(id); }}
                                   className="absolute -right-2 -top-2 z-10 flex h-5 w-5 items-center justify-center rounded-full border border-gray-200 bg-white shadow-sm opacity-0 transition-opacity group-hover:opacity-100 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:hover:bg-gray-600"
                                   aria-label="Edit element"
                            >
                                   <Pencil className="h-2.5 w-2.5 text-gray-500 dark:text-gray-300" aria-hidden="true" />
                            </button>
                     )}
              </div>
       );
};

// ─── Custom node: Group (resizable labeled container) ──────────────────────

interface AnnotationNodeData {
       label: string;
       handlers: React.MutableRefObject<NodeHandlers>;
       readOnly: boolean;
       orientation?: 'horizontal' | 'vertical';
       thickness?: 'slim' | 'wide';
       arrowStart?: boolean;
       arrowEnd?: boolean;
}

const GroupNode: React.FC<NodeProps<AnnotationNodeData>> = ({ id, data, selected }) => (
       <div className="group h-full w-full rounded-xl border-2 border-dashed border-emerald-300/80 bg-emerald-500/[0.06] dark:border-emerald-500/40">
              {!data.readOnly && (
                     <NodeResizer
                            color="#10b981"
                            isVisible={selected}
                            minWidth={140}
                            minHeight={90}
                            onResize={(_e, p) => {
                                   data.handlers.current.onAnnotationResizeLive(id, p.width, p.height);
                            }}
                            onResizeEnd={(_e, p) =>
                                   data.handlers.current.onAnnotationResizeEnd(id, { x: p.x, y: p.y, width: p.width, height: p.height })
                            }
                     />
              )}
              {data.label && (
                     <span className="absolute left-2 top-1.5 rounded bg-emerald-700/90 px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-white">
                            {data.label}
                     </span>
              )}
              {!data.readOnly && (
                     <button
                            type="button"
                            title="Edit"
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => { e.stopPropagation(); data.handlers.current.onRequestEdit(id); }}
                            className="absolute right-1.5 top-1.5 z-10 flex h-5 w-5 items-center justify-center rounded-full border border-emerald-200 bg-white shadow-sm opacity-0 transition-opacity group-hover:opacity-100 hover:bg-emerald-50 dark:border-emerald-700 dark:bg-[#202024] dark:hover:bg-[#27272a]"
                            aria-label="Edit group"
                     >
                            <Pencil className="h-2.5 w-2.5 text-emerald-500" aria-hidden="true" />
                     </button>
              )}
       </div>
);

// ─── Custom node: Divider (labeled boundary line, H/V, slim/wide, arrow) ────

const DIVIDER_COLOR = '#64748b';

const DividerNode: React.FC<NodeProps<AnnotationNodeData>> = ({ id, data, selected }) => {
       const vertical = data.orientation === 'vertical';
       const wide = data.thickness === 'wide';
       const barPx = wide ? 6 : 2;

       return (
              <div className={`group relative flex h-full w-full items-center ${vertical ? 'flex-col justify-center' : 'justify-center'}`}>
                     {!data.readOnly && (
                            <NodeResizer
                                   color={DIVIDER_COLOR}
                                   isVisible={selected}
                                   // Constrain to a single axis so only the LENGTH is editable.
                                   minWidth={vertical ? 24 : 60}
                                   maxWidth={vertical ? 24 : undefined}
                                   minHeight={vertical ? 60 : 24}
                                   maxHeight={vertical ? undefined : 24}
                                   onResize={(_e, p) => {
                                          data.handlers.current.onAnnotationResizeLive(id, p.width, p.height);
                                   }}
                                   onResizeEnd={(_e, p) =>
                                          data.handlers.current.onAnnotationResizeEnd(id, { x: p.x, y: p.y, width: p.width, height: p.height })
                                   }
                            />
                     )}
                     {/* Arrowhead at the START */}
                     {data.arrowStart && (
                            <div
                                   style={
                                          vertical
                                                 ? { width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderBottom: `9px solid ${DIVIDER_COLOR}` }
                                                 : { width: 0, height: 0, borderTop: '6px solid transparent', borderBottom: '6px solid transparent', borderRight: `9px solid ${DIVIDER_COLOR}` }
                                   }
                            />
                     )}
                     {/* The line itself */}
                     <div
                            className="rounded-full bg-gray-400 dark:bg-gray-500"
                            style={vertical ? { width: barPx, flex: 1 } : { height: barPx, flex: 1 }}
                     />
                     {/* Arrowhead at the END */}
                     {data.arrowEnd && (
                            <div
                                   style={
                                          vertical
                                                 ? { width: 0, height: 0, borderLeft: '6px solid transparent', borderRight: '6px solid transparent', borderTop: `9px solid ${DIVIDER_COLOR}` }
                                                 : { width: 0, height: 0, borderTop: '6px solid transparent', borderBottom: '6px solid transparent', borderLeft: `9px solid ${DIVIDER_COLOR}` }
                                   }
                            />
                     )}
                     {/* Optional label */}
                     {data.label && (
                            <span
                                   className={`absolute whitespace-nowrap bg-gray-50 px-1 text-[11px] font-medium uppercase tracking-wide text-gray-500 dark:bg-gray-950 dark:text-gray-400 ${vertical ? 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 rotate-90' : 'left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2'
                                          }`}
                            >
                                   {data.label}
                            </span>
                     )}
                     {!data.readOnly && (
                            <button
                                   type="button"
                                   title="Edit"
                                   onPointerDown={(e) => e.stopPropagation()}
                                   onClick={(e) => { e.stopPropagation(); data.handlers.current.onRequestEdit(id); }}
                                   className="absolute -right-2 -top-2 z-10 flex h-5 w-5 items-center justify-center rounded-full border border-gray-200 bg-white shadow-sm opacity-0 transition-opacity group-hover:opacity-100 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:hover:bg-gray-600"
                                   aria-label="Edit divider"
                            >
                                   <Pencil className="h-2.5 w-2.5 text-gray-500 dark:text-gray-300" aria-hidden="true" />
                            </button>
                     )}
              </div>
       );
};

// ─── Custom node: Text note ────────────────────────────────────────────────

const TextNode: React.FC<NodeProps<AnnotationNodeData>> = ({ id, data, selected }) => (
       <div
              className={`group relative max-w-[240px] rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900 shadow-sm dark:border-amber-900/50 dark:bg-amber-900/20 dark:text-amber-100 ${selected ? 'ring-2 ring-amber-400' : ''
                     }`}
       >
              {data.label || 'Note'}
              {!data.readOnly && (
                     <button
                            type="button"
                            title="Edit"
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => { e.stopPropagation(); data.handlers.current.onRequestEdit(id); }}
                            className="absolute -right-2 -top-2 z-10 flex h-5 w-5 items-center justify-center rounded-full border border-amber-200 bg-white shadow-sm opacity-0 transition-opacity group-hover:opacity-100 hover:bg-amber-50 dark:border-amber-700 dark:bg-gray-800 dark:hover:bg-gray-700"
                            aria-label="Edit note"
                     >
                            <Pencil className="h-2.5 w-2.5 text-amber-500" aria-hidden="true" />
                     </button>
              )}
       </div>
);

const NODE_TYPES_MAP = {
       hld: HldFlowNode,
       group: GroupNode,
       divider: DividerNode,
       text: TextNode,
};

// ─── Mapping helpers ───────────────────────────────────────────────────────

function toRFNode(
       n: DiagramNode,
       handlers: React.MutableRefObject<NodeHandlers>,
       readOnly: boolean
): RFNode<HldNodeData> {
       return {
              id: n.id,
              position: { x: n.position.x, y: n.position.y },
              data: { label: n.label, nodeType: n.type, readOnly, handlers },
              type: 'hld',
              zIndex: 1, // components sit ABOVE annotations
       };
}

function toRFAnnotation(
       a: DiagramAnnotation,
       handlers: React.MutableRefObject<NodeHandlers>,
       readOnly: boolean,
       isSelected: boolean
): RFNode<AnnotationNodeData> {
       // Groups sit behind components (zIndex 0). Text/divider annotations get
       // a higher zIndex so they remain clickable even when overlapping a component.
       // A selected annotation is raised above everything so its resize handles
       // and edit button are always reachable.
       const zIndex = isSelected ? 3 : a.kind === 'group' ? 0 : 2;

       const base: RFNode<AnnotationNodeData> = {
              id: a.id,
              position: { x: a.position.x, y: a.position.y },
              data: {
                     label: a.label ?? '',
                     handlers,
                     readOnly,
                     orientation: a.orientation ?? 'horizontal',
                     thickness: a.thickness ?? 'slim',
                     arrowStart: a.arrowStart ?? false,
                     arrowEnd: a.arrowEnd ?? false,
              },
              type: a.kind,
              draggable: !readOnly,
              connectable: false,
              selectable: !readOnly,
              zIndex,
       };
       if (a.kind === 'group') {
              base.style = { width: a.size?.width ?? DEFAULT_GROUP_SIZE.width, height: a.size?.height ?? DEFAULT_GROUP_SIZE.height };
       } else if (a.kind === 'divider') {
              const vertical = a.orientation === 'vertical';
              // For a vertical divider the LENGTH is the height; width is the hit-area.
              base.style = vertical
                     ? { width: 24, height: a.size?.height ?? DEFAULT_DIVIDER_SIZE.width }
                     : { width: a.size?.width ?? DEFAULT_DIVIDER_SIZE.width, height: 24 };
       }
       return base;
}

function nextSpawnPosition(count: number): { x: number; y: number } {
       const col = count % 4;
       const row = Math.floor(count / 4);
       return { x: 80 + col * 200, y: 80 + row * 130 };
}

function makeNodeId(existing: DiagramNode[]): string {
       const used = new Set(existing.map((n) => n.id));
       let id = '';
       do {
              id = `node_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 7)}`;
       } while (used.has(id));
       return id;
}

// ─── Component ──────────────────────────────────────────────────────────────

const DiagramBuilder: React.FC<DiagramBuilderProps> = ({
       value,
       onChange,
       readOnly = false,
}) => {
       const [rfNodes, setRfNodes, onNodesChange] = useNodesState([]);
       const [rfEdges, setRfEdges, onEdgesChange] = useEdgesState([]);

       // Keep a ref to the latest value so the edge-change handler never goes stale.
       const valueRef = useRef(value);
       useEffect(() => { valueRef.current = value; }, [value]);

       const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
       const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
       const [selectedAnnotationId, setSelectedAnnotationId] = useState<string | null>(null);
       const [error, setError] = useState<DiagramError | null>(null);
       const [isFullscreen, setIsFullscreen] = useState(false);

       const rfInstanceRef = useRef<ReactFlowInstance | null>(null);

       // Tracks last JSON we emitted — prevents a sync loop when value echoes back.
       const lastEmittedRef = useRef<string>('');

       // Stable handler bag for custom nodes (updated each render, read at call time).
       const handlersRef = useRef<NodeHandlers>({
              onAnnotationResizeEnd: () => { },
              onAnnotationResizeLive: () => { },
              onRequestEdit: () => { },
       });

       // While a GROUP is being dragged, remember which components were inside it
       // (and their start positions) so we can move them LIVE with the group.
       const dragCtxRef = useRef<{
              groupId: string;
              groupStart: { x: number; y: number };
              children: Map<string, { x: number; y: number }>;
       } | null>(null);

       // Build the full React Flow node list: annotations first (behind), then
       // components (on top). Recomputed whenever `value` changes from OUTSIDE.
       const toEdge = useCallback((e: DiagramEdge, isSelected?: boolean): RFEdge => {
              const parts = [e.label, e.protocol].filter((p): p is string => typeof p === 'string' && p.length > 0);
              return {
                     id: e.id,
                     source: e.source,
                     target: e.target,
                     label: parts.join(' · ') || undefined,
                     ...(e.bidirectional ? { markerStart: { type: MarkerType.ArrowClosed, width: 10, height: 10 } } : {}),
                     markerEnd: { type: MarkerType.ArrowClosed, width: 10, height: 10 },
                     style: {
                            strokeWidth: isSelected ? 2.5 : 1.5,
                     },
                     selected: isSelected,
              };
       }, []);

       const buildRFNodes = useCallback(
              (state: DiagramState, selectedAnnId?: string | null): RFNode[] => {
                     const anns = (state.annotations ?? []).map((a) =>
                            toRFAnnotation(a, handlersRef, readOnly, a.id === selectedAnnId)
                     );
                     const comps = state.nodes.map((n) => toRFNode(n, handlersRef, readOnly));
                     return [...anns, ...comps];
              },
              [readOnly]
       );

       useEffect(() => {
              const canonical = JSON.stringify(serialize(value));
              if (canonical === lastEmittedRef.current) return;
              lastEmittedRef.current = canonical;
              setRfNodes(buildRFNodes(value, selectedAnnotationId));
              setRfEdges(value.edges.map((e) => toEdge(e, e.id === selectedEdgeId)));
       }, [value, buildRFNodes, toEdge, setRfNodes, setRfEdges, selectedAnnotationId, selectedEdgeId]);

       /** Rehydrate React Flow from a new authoritative state + push it up. */
       const applyState = useCallback(
              (next: DiagramState) => {
                     const serialized = serialize(next);
                     lastEmittedRef.current = JSON.stringify(serialized);
                     setRfNodes(buildRFNodes(serialized, selectedAnnotationId));
                     setRfEdges(serialized.edges.map((e) => toEdge(e, e.id === selectedEdgeId)));
                     onChange(serialized);
              },
              [onChange, buildRFNodes, toEdge, setRfNodes, setRfEdges, selectedAnnotationId, selectedEdgeId]
       );

       /**
        * Commit an in-place edit (label/protocol/annotation relabel/divider option)
        * WITHOUT rehydrating the full canvas, so the current selection is preserved
        * and the inspector stays open.
        */
       const applyInPlaceEdit = useCallback(
              (next: DiagramState) => {
                     const serialized = serialize(next);
                     lastEmittedRef.current = JSON.stringify(serialized);
                     // Patch only the data fields of changed nodes/edges — keep React Flow
                     // node instances (and their `selected` flag) intact.
                     setRfNodes((prev) =>
                            prev.map((rfn) => {
                                   const ann = (serialized.annotations ?? []).find((a) => a.id === rfn.id);
                                   if (ann) {
                                          // Compute the correct style for this annotation kind
                                          let styleUpdate: Record<string, unknown> = {};
                                          if (ann.kind === 'group') {
                                                 styleUpdate = { width: ann.size?.width ?? DEFAULT_GROUP_SIZE.width, height: ann.size?.height ?? DEFAULT_GROUP_SIZE.height };
                                          } else if (ann.kind === 'divider') {
                                                 const vertical = ann.orientation === 'vertical';
                                                 styleUpdate = vertical
                                                        ? { width: 24, height: ann.size?.height ?? DEFAULT_DIVIDER_SIZE.width }
                                                        : { width: ann.size?.width ?? DEFAULT_DIVIDER_SIZE.width, height: 24 };
                                          }
                                          return {
                                                 ...rfn,
                                                 data: {
                                                        ...rfn.data,
                                                        label: ann.label ?? '',
                                                        orientation: ann.orientation ?? 'horizontal',
                                                        thickness: ann.thickness ?? 'slim',
                                                        arrowStart: ann.arrowStart ?? false,
                                                        arrowEnd: ann.arrowEnd ?? false,
                                                 },
                                                 style: { ...rfn.style, ...styleUpdate },
                                          };
                                   }
                                   const nd = serialized.nodes.find((n) => n.id === rfn.id);
                                   if (nd) {
                                          return { ...rfn, data: { ...rfn.data, label: nd.label } };
                                   }
                                   return rfn;
                            })
                     );
                     setRfEdges((prev) =>
                            prev.map((rfe) => {
                                   const e = serialized.edges.find((se) => se.id === rfe.id);
                                   return e ? toEdge(e, e.id === selectedEdgeId) : rfe;
                            })
                     );
                     onChange(serialized);
              },
              [onChange, toEdge, setRfNodes, setRfEdges, selectedEdgeId]
       );

       /** Push state up WITHOUT rehydrating React Flow (keeps live selection). */
       const emitWithoutRehydrate = useCallback(
              (next: DiagramState) => {
                     const serialized = serialize(next);
                     lastEmittedRef.current = JSON.stringify(serialized);
                     onChange(serialized);
              },
              [onChange]
       );

       /**
        * Intercept React Flow edge changes — pull out 'remove' events and apply
        * them to `value` ourselves so deleted edges don't snap back on the next
        * render. All other change types pass through to the built-in handler.
        */
       const handleEdgesChange = useCallback(
              (changes: EdgeChange[]) => {
                     const removes = changes.filter((c): c is { type: 'remove'; id: string } => c.type === 'remove');
                     const rest = changes.filter((c) => c.type !== 'remove');

                     if (removes.length > 0) {
                            const removedIds = new Set(removes.map((c) => c.id));
                            const next: DiagramState = {
                                   ...valueRef.current,
                                   edges: valueRef.current.edges.filter((e) => !removedIds.has(e.id)),
                            };
                            const serialized = serialize(next);
                            lastEmittedRef.current = JSON.stringify(serialized);
                            setSelectedEdgeId(null);
                            setError(null);
                            setRfEdges((prev) => applyEdgeChanges(removes, prev));
                            onChange(serialized);
                     }

                     if (rest.length > 0) {
                            onEdgesChange(rest);
                     }
              },
              [onChange, onEdgesChange, setRfEdges]
       );

       // ── Component node ops ────────────────────────────────────────────────────

       const handleAddNode = useCallback(
              (type: NodeType) => {
                     setError(null);
                     const node: DiagramNode = {
                            id: makeNodeId(value.nodes),
                            type,
                            label: NODE_TYPE_META[type].label,
                            position: nextSpawnPosition(value.nodes.length),
                     };
                     const result = addNode(value, node);
                     if (!result.ok) return setError(result.error);
                     applyState(result.state);
              },
              [value, applyState]
       );

       const handleRenameNode = useCallback(
              (nodeId: string, label: string) => {
                     const result = setNodeLabel(value, nodeId, label);
                     if (!result.ok) return setError(result.error);
                     setError(null);
                     applyInPlaceEdit(result.state);
              },
              [value, applyInPlaceEdit]
       );

       const handleDeleteNode = useCallback(
              (nodeId: string) => {
                     applyState(removeNode(value, nodeId));
                     setSelectedNodeId(null);
                     setError(null);
              },
              [value, applyState]
       );

       // ── Annotation ops ────────────────────────────────────────────────────────

       const handleAddAnnotation = useCallback(
              (kind: AnnotationKind) => {
                     const existing = value.annotations ?? [];
                     const ann: DiagramAnnotation = {
                            id: makeAnnotationId(existing),
                            kind,
                            position: { x: 120 + existing.length * 24, y: 120 + existing.length * 24 },
                            label:
                                   kind === 'group' ? 'Group' : kind === 'divider' ? '' : 'Note',
                     };
                     if (kind === 'group') ann.size = { ...DEFAULT_GROUP_SIZE };
                     if (kind === 'divider') ann.size = { ...DEFAULT_DIVIDER_SIZE };
                     applyState(addAnnotation(value, ann));
              },
              [value, applyState]
       );

       const handleRelabelAnnotation = useCallback(
              (id: string, label: string) => {
                     applyInPlaceEdit(updateAnnotation(value, id, { label: label.trim().slice(0, 120) }));
              },
              [value, applyInPlaceEdit]
       );

       const handleDeleteAnnotation = useCallback(
              (id: string) => {
                     applyState(removeAnnotation(value, id));
                     setSelectedAnnotationId(null);
              },
              [value, applyState]
       );

       // Update a divider's orientation / thickness / arrow. When orientation
       // flips, preserve the visible LENGTH (long dimension) in the new axis.
       const handleUpdateDivider = useCallback(
              (
                     id: string,
                     patch: { orientation?: 'horizontal' | 'vertical'; thickness?: 'slim' | 'wide'; arrowStart?: boolean; arrowEnd?: boolean }
              ) => {
                     const cur = (value.annotations ?? []).find((a) => a.id === id);
                     let sizePatch: { size?: { width: number; height: number } } = {};
                     if (
                            cur?.size &&
                            patch.orientation &&
                            patch.orientation !== (cur.orientation ?? 'horizontal')
                     ) {
                            // The current orientation determines which dimension is the LENGTH.
                            // horizontal → length is width; vertical → length is height.
                            // After flipping, the length becomes the OTHER dimension.
                            const currentlyHorizontal = (cur.orientation ?? 'horizontal') === 'horizontal';
                            const length = currentlyHorizontal ? cur.size.width : cur.size.height;
                            // New orientation: length goes into the new long axis, hit-area stays 24px
                            sizePatch = currentlyHorizontal
                                   ? { size: { width: 24, height: length } }   // H→V: length becomes height
                                   : { size: { width: length, height: 24 } };  // V→H: length becomes width
                     }
                     applyInPlaceEdit(updateAnnotation(value, id, { ...patch, ...sizePatch }));
              },
              [value, applyInPlaceEdit]
       );

       // Keep the resize handler current for custom nodes reading via the ref.
       handlersRef.current.onAnnotationResizeLive = (id, width, height) => {
              // Update the React Flow node style in-place for smooth real-time feedback.
              setRfNodes((prev) =>
                     prev.map((n) =>
                            n.id === id ? { ...n, style: { ...n.style, width, height } } : n
                     )
              );
       };
       handlersRef.current.onAnnotationResizeEnd = (id, box) => {
              applyInPlaceEdit(
                     updateAnnotation(value, id, {
                            position: { x: box.x, y: box.y },
                            size: { width: box.width, height: box.height },
                     })
              );
       };
       handlersRef.current.onRequestEdit = (id) => {
              const annIds = new Set((value.annotations ?? []).map((a) => a.id));
              if (annIds.has(id)) {
                     setSelectedAnnotationId(id);
                     setSelectedNodeId(null);
                     setSelectedEdgeId(null);
              } else {
                     setSelectedNodeId(id);
                     setSelectedAnnotationId(null);
                     setSelectedEdgeId(null);
              }
       };

       // ── Edge ops ────────────────────────────────────────────────────────────

       const handleConnect = useCallback(
              (connection: Connection) => {
                     setError(null);
                     if (!connection.source || !connection.target) return;
                     const result = addEdge(value, connection.source, connection.target);
                     if (!result.ok) return setError(result.error);
                     applyState(result.state);
              },
              [value, applyState]
       );

       const handleRelabelEdge = useCallback(
              (edgeId: string, label: string) => {
                     const result = setEdgeLabel(value, edgeId, label);
                     if (!result.ok) return setError(result.error);
                     setError(null);
                     applyInPlaceEdit(result.state);
              },
              [value, applyInPlaceEdit]
       );

       const handleSetEdgeProtocol = useCallback(
              (edgeId: string, protocol: EdgeProtocol) => {
                     const result = setEdgeProtocol(value, edgeId, protocol);
                     if (!result.ok) return setError(result.error);
                     setError(null);
                     applyInPlaceEdit(result.state);
              },
              [value, applyInPlaceEdit]
       );

       const handleSetEdgeBidirectional = useCallback(
              (edgeId: string, bidirectional: boolean) => {
                     const result = setEdgeBidirectional(value, edgeId, bidirectional);
                     if (!result.ok) return;
                     applyInPlaceEdit(result.state);
              },
              [value, applyInPlaceEdit]
       );

       // ── Drag: move a group's contents LIVE, then persist on drop ──────────────

       const handleNodeDragStart = useCallback(
              (_e: React.MouseEvent, node: RFNode) => {
                     const grp = (value.annotations ?? []).find(
                            (a) => a.id === node.id && a.kind === 'group'
                     );
                     if (!grp) {
                            dragCtxRef.current = null;
                            return;
                     }
                     const gx = grp.position.x;
                     const gy = grp.position.y;
                     const gw = grp.size?.width ?? DEFAULT_GROUP_SIZE.width;
                     const gh = grp.size?.height ?? DEFAULT_GROUP_SIZE.height;
                     const children = new Map<string, { x: number; y: number }>();

                     rfNodes.forEach((n) => {
                            if (n.id === grp.id) return; // skip the group itself
                            const w = n.width ?? 140;
                            const h = n.height ?? 48;
                            const cx = n.position.x + w / 2;
                            const cy = n.position.y + h / 2;
                            // Include BOTH architecture components (hld) AND annotations
                            // (text, divider) whose center falls inside the group bounds.
                            if (cx >= gx && cx <= gx + gw && cy >= gy && cy <= gy + gh) {
                                   children.set(n.id, { x: n.position.x, y: n.position.y });
                            }
                     });
                     dragCtxRef.current = {
                            groupId: node.id,
                            groupStart: { x: node.position.x, y: node.position.y },
                            children,
                     };
              },
              [value.annotations, rfNodes]
       );

       const handleNodeDrag = useCallback(
              (_e: React.MouseEvent, node: RFNode) => {
                     const ctx = dragCtxRef.current;
                     if (!ctx || node.id !== ctx.groupId || ctx.children.size === 0) return;
                     const dx = node.position.x - ctx.groupStart.x;
                     const dy = node.position.y - ctx.groupStart.y;
                     // Move the contained components live, in lockstep with the group.
                     setRfNodes((prev) =>
                            prev.map((n) => {
                                   const start = ctx.children.get(n.id);
                                   return start ? { ...n, position: { x: start.x + dx, y: start.y + dy } } : n;
                            })
                     );
              },
              [setRfNodes]
       );

       const handleNodeDragStop = useCallback(() => {
              dragCtxRef.current = null;
              // React Flow already reflects the final positions of the dragged node
              // and (for a group) its live-moved children — just persist them.
              const posById = new Map(rfNodes.map((n) => [n.id, n.position]));
              const anns = value.annotations ?? [];
              const annIds = new Set(anns.map((a) => a.id));
              const next: DiagramState = {
                     nodes: value.nodes.map((n) => {
                            const p = posById.get(n.id);
                            return p ? { ...n, position: { x: p.x, y: p.y } } : n;
                     }),
                     edges: value.edges,
                     annotations: anns.map((a) => {
                            const p = posById.get(a.id);
                            return p && annIds.has(a.id) ? { ...a, position: { x: p.x, y: p.y } } : a;
                     }),
              };
              emitWithoutRehydrate(next);
       }, [rfNodes, value, emitWithoutRehydrate]);

       // ── Keyboard delete → route to the right remover ──────────────────────────

       const handleNodesDelete = useCallback(
              (deleted: RFNode[]) => {
                     const deletedIds = new Set(deleted.map((n) => n.id));
                     const annIds = new Set((value.annotations ?? []).map((a) => a.id));
                     let next = value;
                     deletedIds.forEach((id) => {
                            next = annIds.has(id) ? removeAnnotation(next, id) : removeNode(next, id);
                     });
                     setSelectedNodeId(null);
                     setSelectedAnnotationId(null);
                     setError(null);
                     applyState(next);
              },
              [value, applyState]
       );

       const handleEdgesDelete = useCallback(
              (deleted: RFEdge[]) => {
                     const deletedIds = new Set(deleted.map((e) => e.id));
                     const next: DiagramState = {
                            ...value,
                            edges: value.edges.filter((e) => !deletedIds.has(e.id)),
                     };
                     setSelectedEdgeId(null);
                     setError(null);
                     applyState(next);
              },
              [value, applyState]
       );

       const handleDeleteEdge = useCallback(
              (edgeId: string) => {
                     const next: DiagramState = {
                            ...value,
                            edges: value.edges.filter((e) => e.id !== edgeId),
                     };
                     setSelectedEdgeId(null);
                     setError(null);
                     applyState(next);
              },
              [value, applyState]
       );

       const handleSelectionChange = useCallback(
              (params: OnSelectionChangeParams) => {
                     const annIds = new Set((value.annotations ?? []).map((a) => a.id));
                     const one = params.nodes.length === 1 ? params.nodes[0].id : null;
                     setSelectedNodeId(one && !annIds.has(one) ? one : null);
                     setSelectedAnnotationId(one && annIds.has(one) ? one : null);
                     setSelectedEdgeId(params.edges.length === 1 ? params.edges[0].id : null);
              },
              [value.annotations]
       );

       const selectedNode = useMemo(
              () => value.nodes.find((n) => n.id === selectedNodeId) ?? null,
              [value.nodes, selectedNodeId]
       );
       const selectedEdge = useMemo(
              () => value.edges.find((e) => e.id === selectedEdgeId) ?? null,
              [value.edges, selectedEdgeId]
       );
       const selectedAnnotation = useMemo(
              () => (value.annotations ?? []).find((a) => a.id === selectedAnnotationId) ?? null,
              [value.annotations, selectedAnnotationId]
       );

       // ── Full-screen ───────────────────────────────────────────────────────────

       useEffect(() => {
              if (!isFullscreen) return;
              const onKey = (e: KeyboardEvent) => {
                     if (e.key === 'Escape') setIsFullscreen(false);
              };
              window.addEventListener('keydown', onKey);
              return () => window.removeEventListener('keydown', onKey);
       }, [isFullscreen]);

       useEffect(() => {
              const inst = rfInstanceRef.current;
              if (!inst) return;
              const timer = window.setTimeout(() => {
                     inst.fitView({ padding: 0.15, duration: 200, maxZoom: 1 });
              }, 90);
              return () => window.clearTimeout(timer);
       }, [isFullscreen]);

       return (
              <div
                     className={
                            isFullscreen
                                   ? 'fixed inset-0 z-[60] flex flex-col bg-gray-50 dark:bg-[#09090b]'
                                   : 'flex h-full min-h-[24rem] w-full flex-col bg-gray-50 dark:bg-[#09090b]'
                     }
              >
                     {/* Top bar: palette (edit mode) + full-screen toggle (always) */}
                     <div className="flex flex-shrink-0 flex-wrap items-center gap-1.5 border-b border-gray-200 bg-white px-3 py-2 dark:border-[#3f3f46] dark:bg-[#18181b]">
                            {!readOnly && (
                                   <>
                                          <span className="mr-1 inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
                                                 <Plus className="h-3.5 w-3.5" aria-hidden="true" /> Add
                                          </span>
                                          {NODE_TYPES.map((type) => {
                                                 const meta = NODE_TYPE_META[type];
                                                 const Icon = meta.Icon;
                                                 return (
                                                        <button
                                                               key={type}
                                                               type="button"
                                                               onClick={() => handleAddNode(type)}
                                                               className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-700 transition-colors hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-200 dark:hover:bg-gray-800"
                                                        >
                                                               <span style={{ color: meta.color }} className="flex items-center">
                                                                      <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                                                               </span>
                                                               {meta.label}
                                                        </button>
                                                 );
                                          })}

                                          {/* Layout helpers */}
                                          <span className="mx-1 h-5 w-px bg-gray-200 dark:bg-gray-700" />
                                          <button type="button" onClick={() => handleAddAnnotation('group')} title="Add a group container" className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
                                                 <Square className="h-3.5 w-3.5 text-emerald-500" aria-hidden="true" /> Group
                                          </button>
                                          <button type="button" onClick={() => handleAddAnnotation('divider')} title="Add a divider line" className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
                                                 <Minus className="h-3.5 w-3.5 text-gray-500" aria-hidden="true" /> Divider
                                          </button>
                                          <button type="button" onClick={() => handleAddAnnotation('text')} title="Add a text note" className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">
                                                 <TypeIcon className="h-3.5 w-3.5 text-amber-500" aria-hidden="true" /> Text
                                          </button>
                                   </>
                            )}
                            <button
                                   type="button"
                                   onClick={() => setIsFullscreen((v) => !v)}
                                   title={isFullscreen ? 'Exit full screen (Esc)' : 'Full screen'}
                                   aria-label={isFullscreen ? 'Exit full screen' : 'Full screen'}
                                   className="ml-auto inline-flex items-center gap-1.5 rounded-lg border border-gray-200 px-2.5 py-1 text-xs font-medium text-gray-600 transition-colors hover:border-gray-300 hover:bg-gray-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800"
                            >
                                   {isFullscreen ? (
                                          <>
                                                 <Minimize2 className="h-3.5 w-3.5" aria-hidden="true" /> Exit full screen
                                          </>
                                   ) : (
                                          <>
                                                 <Maximize2 className="h-3.5 w-3.5" aria-hidden="true" /> Full screen
                                          </>
                                   )}
                            </button>
                     </div>

                     {/* Canvas */}
                     <div className="relative min-h-0 flex-1">
                            <ReactFlow
                                   nodes={rfNodes}
                                   edges={rfEdges}
                                   nodeTypes={NODE_TYPES_MAP}
                                   onInit={(inst) => {
                                          rfInstanceRef.current = inst;
                                   }}
                                   onNodesChange={onNodesChange}
                                   onEdgesChange={handleEdgesChange}
                                   onConnect={handleConnect}
                                   onNodeDragStart={handleNodeDragStart}
                                   onNodeDrag={handleNodeDrag}
                                   onNodeDragStop={handleNodeDragStop}
                                   onNodesDelete={handleNodesDelete}
                                   onEdgesDelete={handleEdgesDelete}
                                   onEdgeClick={(_e, edge) => {
                                          setSelectedEdgeId(edge.id);
                                          setSelectedNodeId(null);
                                          setSelectedAnnotationId(null);
                                   }}
                                   onSelectionChange={handleSelectionChange}
                                   nodesConnectable={!readOnly}
                                   nodesDraggable={!readOnly}
                                   elementsSelectable
                                   deleteKeyCode={readOnly ? null : ['Backspace', 'Delete']}
                                   // React Flow repeatedly recalculates an empty viewport when
                                   // fitView is enabled without any nodes or annotations. A new
                                   // custom workspace deliberately starts blank, so only fit once
                                   // there is an actual diagram to frame.
                                   fitView={value.nodes.length > 0 || (value.annotations ?? []).length > 0}
                                   fitViewOptions={{ padding: 0.15, maxZoom: 1 }}
                                   minZoom={0.3}
                                   maxZoom={2}
                                   zoomOnScroll
                                   panOnScroll={false}
                                   proOptions={{ hideAttribution: true }}
                            >
                                   <Background variant={BackgroundVariant.Dots} gap={20} size={1} className="!bg-gray-50 dark:!bg-[#09090b]" color="#3f3f46" />
                                   <Controls
                                          showInteractive={false}
                                          fitViewOptions={{ padding: 0.25, maxZoom: 1 }}
                                          className="!rounded-lg !border !border-gray-200 !shadow-sm dark:!border-[#3f3f46] dark:!bg-[#202024]"
                                   />
                            </ReactFlow>

                            {/* Error toast */}
                            {error && (
                                   <div role="alert" className="absolute left-1/2 top-3 z-20 flex max-w-md -translate-x-1/2 items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700 shadow-md dark:border-red-900/50 dark:bg-red-950/60 dark:text-red-300">
                                          <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" aria-hidden="true" />
                                          <span className="flex-1">{error.message}</span>
                                          <button type="button" onClick={() => setError(null)} className="flex-shrink-0 rounded p-0.5 hover:bg-red-100 dark:hover:bg-red-900/40" aria-label="Dismiss error">
                                                 <X className="h-3.5 w-3.5" aria-hidden="true" />
                                          </button>
                                   </div>
                            )}

                            {/* Empty-canvas prompt */}
                            {value.nodes.length === 0 && (value.annotations ?? []).length === 0 && (
                                   <div className="pointer-events-none absolute inset-0 z-0 flex flex-col items-center justify-center px-6 text-center">
                                          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400">
                                                 <Server className="h-7 w-7" aria-hidden="true" />
                                          </div>
                                          <p className="mt-3 text-sm font-semibold text-gray-600 dark:text-gray-300">Build your architecture</p>
                                          <p className="mt-1 max-w-xs text-xs text-gray-500 dark:text-gray-500">
                                                 Add components from the palette above, then drag from a node's right edge to another node's left edge to
                                                 connect them. Use Group / Divider / Text to organize the canvas.
                                          </p>
                                   </div>
                            )}

                            {/* Node inspector */}
                            {!readOnly && selectedNode && (
                                   <div className="absolute right-3 top-3 z-10 w-60 rounded-xl border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-700 dark:bg-gray-800">
                                          <div className="mb-2 flex items-center justify-between">
                                                 <span className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide" style={{ color: NODE_TYPE_META[selectedNode.type].color }}>
                                                        {React.createElement(NODE_TYPE_META[selectedNode.type].Icon, { className: 'h-3.5 w-3.5', 'aria-hidden': true })}
                                                        {NODE_TYPE_META[selectedNode.type].label}
                                                 </span>
                                                 <div className="flex items-center gap-1">
                                                        <button type="button" onClick={() => { handleDeleteNode(selectedNode.id); setSelectedNodeId(null); }} className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40">
                                                               <Trash2 className="h-3.5 w-3.5" aria-hidden="true" /> Delete
                                                        </button>
                                                        <button type="button" onClick={() => setSelectedNodeId(null)} className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200" aria-label="Close inspector">
                                                               <X className="h-3.5 w-3.5" aria-hidden="true" />
                                                        </button>
                                                 </div>
                                          </div>
                                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
                                                 Label
                                                 <input
                                                        key={selectedNode.id}
                                                        type="text"
                                                        defaultValue={selectedNode.label}
                                                        maxLength={60}
                                                        onBlur={(e) => handleRenameNode(selectedNode.id, e.target.value)}
                                                        onKeyDown={(e) => {
                                                               if (e.key === 'Enter') {
                                                                      handleRenameNode(selectedNode.id, e.currentTarget.value);
                                                                      setSelectedNodeId(null);
                                                               }
                                                        }}
                                                        className="mt-1 w-full rounded-md border border-gray-200 bg-white px-2 py-1 text-sm text-gray-900 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                                                 />
                                          </label>
                                   </div>
                            )}

                            {/* Annotation inspector */}
                            {!readOnly && !selectedNode && selectedAnnotation && (
                                   <div className="absolute right-3 top-3 z-10 w-60 rounded-xl border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-700 dark:bg-gray-800">
                                          <div className="mb-2 flex items-center justify-between">
                                                 <span className="text-xs font-semibold uppercase tracking-wide text-gray-400 capitalize">{selectedAnnotation.kind}</span>
                                                 <div className="flex items-center gap-1">
                                                        <button type="button" onClick={() => { handleDeleteAnnotation(selectedAnnotation.id); setSelectedAnnotationId(null); }} className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40">
                                                               <Trash2 className="h-3.5 w-3.5" aria-hidden="true" /> Delete
                                                        </button>
                                                        <button type="button" onClick={() => setSelectedAnnotationId(null)} className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200" aria-label="Close inspector">
                                                               <X className="h-3.5 w-3.5" aria-hidden="true" />
                                                        </button>
                                                 </div>
                                          </div>
                                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
                                                 Label
                                                 <input
                                                        key={selectedAnnotation.id}
                                                        type="text"
                                                        defaultValue={selectedAnnotation.label ?? ''}
                                                        maxLength={120}
                                                        onBlur={(e) => handleRelabelAnnotation(selectedAnnotation.id, e.target.value)}
                                                        onKeyDown={(e) => {
                                                               if (e.key === 'Enter') {
                                                                      handleRelabelAnnotation(selectedAnnotation.id, e.currentTarget.value);
                                                                      setSelectedAnnotationId(null);
                                                               }
                                                        }}
                                                        className="mt-1 w-full rounded-md border border-gray-200 bg-white px-2 py-1 text-sm text-gray-900 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                                                 />
                                          </label>
                                          {selectedAnnotation.kind === 'group' && (
                                                 <p className="mt-2 text-[11px] text-gray-400">Drag the corners to resize. Drop components inside, then drag the group to move them together.</p>
                                          )}
                                          {selectedAnnotation.kind === 'divider' && (
                                                 <div className="mt-3 space-y-2">
                                                        <div>
                                                               <span className="mb-1 block text-[11px] font-medium text-gray-600 dark:text-gray-400">Orientation</span>
                                                               <div className="inline-flex rounded-md border border-gray-200 p-0.5 dark:border-gray-700">
                                                                      {(['horizontal', 'vertical'] as const).map((o) => (
                                                                             <button
                                                                                    key={o}
                                                                                    type="button"
                                                                                    onClick={() => handleUpdateDivider(selectedAnnotation.id, { orientation: o })}
                                                                                    className={`rounded px-2 py-0.5 text-xs font-medium capitalize transition-colors ${(selectedAnnotation.orientation ?? 'horizontal') === o ? 'bg-blue-600 text-white' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                                                                             >
                                                                                    {o}
                                                                             </button>
                                                                      ))}
                                                               </div>
                                                        </div>
                                                        <div>
                                                               <span className="mb-1 block text-[11px] font-medium text-gray-600 dark:text-gray-400">Thickness</span>
                                                               <div className="inline-flex rounded-md border border-gray-200 p-0.5 dark:border-gray-700">
                                                                      {(['slim', 'wide'] as const).map((t) => (
                                                                             <button
                                                                                    key={t}
                                                                                    type="button"
                                                                                    onClick={() => handleUpdateDivider(selectedAnnotation.id, { thickness: t })}
                                                                                    className={`rounded px-2 py-0.5 text-xs font-medium capitalize transition-colors ${(selectedAnnotation.thickness ?? 'slim') === t ? 'bg-blue-600 text-white' : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'}`}
                                                                             >
                                                                                    {t}
                                                                             </button>
                                                                      ))}
                                                               </div>
                                                        </div>
                                                        <div>
                                                               <span className="mb-1 block text-[11px] font-medium text-gray-600 dark:text-gray-400">Arrowheads</span>
                                                               <div className="flex gap-2">
                                                                      <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-gray-300">
                                                                             <input
                                                                                    type="checkbox"
                                                                                    checked={!!selectedAnnotation.arrowStart}
                                                                                    onChange={(e) => handleUpdateDivider(selectedAnnotation.id, { arrowStart: e.target.checked })}
                                                                                    className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                                             />
                                                                             Start
                                                                      </label>
                                                                      <label className="flex items-center gap-1.5 text-xs font-medium text-gray-600 dark:text-gray-300">
                                                                             <input
                                                                                    type="checkbox"
                                                                                    checked={!!selectedAnnotation.arrowEnd}
                                                                                    onChange={(e) => handleUpdateDivider(selectedAnnotation.id, { arrowEnd: e.target.checked })}
                                                                                    className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                                             />
                                                                             End
                                                                      </label>
                                                               </div>
                                                        </div>
                                                        <p className="text-[11px] text-gray-400">Drag the handle to change length.</p>
                                                 </div>
                                          )}
                                   </div>
                            )}

                            {/* Edge inspector */}
                            {!readOnly && !selectedNode && !selectedAnnotation && selectedEdge && (
                                   <div className="absolute right-3 top-3 z-10 w-60 rounded-xl border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-700 dark:bg-gray-800">
                                          <div className="mb-2 flex items-center justify-between">
                                                 <span className="text-xs font-semibold uppercase tracking-wide text-gray-400">Connection</span>
                                                 <div className="flex items-center gap-1">
                                                        <button type="button" onClick={() => handleDeleteEdge(selectedEdge.id)} className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs font-medium text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40">
                                                               <Trash2 className="h-3.5 w-3.5" aria-hidden="true" /> Delete
                                                        </button>
                                                        <button type="button" onClick={() => setSelectedEdgeId(null)} className="rounded p-0.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-200" aria-label="Close inspector">
                                                               <X className="h-3.5 w-3.5" aria-hidden="true" />
                                                        </button>
                                                 </div>
                                          </div>
                                          <label className="block text-xs font-medium text-gray-600 dark:text-gray-400">
                                                 Label
                                                 <input
                                                        key={selectedEdge.id}
                                                        type="text"
                                                        defaultValue={selectedEdge.label ?? ''}
                                                        maxLength={60}
                                                        onBlur={(e) => handleRelabelEdge(selectedEdge.id, e.target.value)}
                                                        onKeyDown={(e) => {
                                                               if (e.key === 'Enter') {
                                                                      handleRelabelEdge(selectedEdge.id, e.currentTarget.value);
                                                                      setSelectedEdgeId(null);
                                                               }
                                                        }}
                                                        className="mt-1 w-full rounded-md border border-gray-200 bg-white px-2 py-1 text-sm text-gray-900 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                                                 />
                                          </label>
                                          <label className="mt-2 block text-xs font-medium text-gray-600 dark:text-gray-400">
                                                 Protocol
                                                 <select value={selectedEdge.protocol ?? ''} onChange={(e) => e.target.value && handleSetEdgeProtocol(selectedEdge.id, e.target.value as EdgeProtocol)} className="mt-1 w-full rounded-md border border-gray-200 bg-white px-2 py-1 text-sm text-gray-900 focus:border-blue-400 focus:outline-none focus:ring-1 focus:ring-blue-400 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100">
                                                        <option value="">—</option>
                                                        {EDGE_PROTOCOLS.map((p) => (
                                                               <option key={p} value={p}>{p}</option>
                                                        ))}
                                                 </select>
                                          </label>
                                          <label className="mt-2 flex items-center gap-2 text-xs font-medium text-gray-600 dark:text-gray-300">
                                                 <input
                                                        type="checkbox"
                                                        checked={!!selectedEdge.bidirectional}
                                                        onChange={(e) => handleSetEdgeBidirectional(selectedEdge.id, e.target.checked)}
                                                        className="h-3.5 w-3.5 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                                                 />
                                                 Bidirectional
                                          </label>
                                   </div>
                            )}
                     </div>
              </div>
       );
};

export default DiagramBuilder;
